/**
 * CUSTOMIZATION of @plone/volto Sidebar.jsx (Volto 19.1.5).
 *
 * Adds an "Assistant" tab hosting the AI editor assistant panel, and
 * reacts to the assistant's pendingFocus flag (set when the user grants
 * write permission from view mode or clicks the toolbar assistant button
 * in edit mode) by expanding the sidebar and activating the tab.
 *
 * The tab only exists on content editing routes — control panels (content
 * type layouts and the like) also render this Sidebar, but there is no
 * page for the assistant to talk about there.
 *
 * Upstream changes: panes extracted into a variable, assistant pane
 * appended, pendingFocus effect added. Everything else is verbatim.
 */
import React, { useState, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Tab } from 'semantic-ui-react';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@interaktiv/volto-ai-editor/store';
import { useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { withCookies } from 'react-cookie';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'classnames';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';
import { getCookieOptions } from '@plone/volto/helpers/Cookies/cookies';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import forbiddenSVG from '@plone/volto/icons/forbidden.svg';
import { setSidebarTab } from '@plone/volto/actions/sidebar/sidebar';
import expandSVG from '@plone/volto/icons/left-key.svg';
import collapseSVG from '@plone/volto/icons/right-key.svg';
import AssistantPanel from 'volto-uni-bonn/components/Lino/LinoPanel';
import {
  setAssistantPendingFocus,
  setAssistantTabIndex,
} from '@interaktiv/volto-ai-editor/actions/assistant';

const messages = defineMessages({
  document: {
    id: 'Document',
    defaultMessage: 'Document',
  },
  block: {
    id: 'Block',
    defaultMessage: 'Block',
  },
  settings: {
    id: 'Settings',
    defaultMessage: 'Settings',
  },
  shrinkSidebar: {
    id: 'Shrink sidebar',
    defaultMessage: 'Shrink sidebar',
  },
  expandSidebar: {
    id: 'Expand sidebar',
    defaultMessage: 'Expand sidebar',
  },
  order: {
    id: 'Order',
    defaultMessage: 'Order',
  },
  assistant: {
    id: 'Lino',
    defaultMessage: 'Lino',
  },
});

const Sidebar = (props: any) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {
    cookies,
    content,
    documentTab,
    blockTab,
    settingsTab,
    orderTab = true,
  } = props;
  const [expanded, setExpanded] = useState(
    cookies.get('sidebar_expanded') !== 'false',
  );
  const [size] = useState(0);
  const [showFull, setshowFull] = useState(true);

  const location = useLocation();
  const assistantEnabled = !location.pathname.startsWith('/controlpanel');

  const tab = useAppSelector((state) => state.sidebar.tab);
  const toolbarExpanded = useAppSelector((state) => state.toolbar.expanded);
  const type = useAppSelector((state) => state.schema?.schema?.title);
  const assistantPendingFocus = useAppSelector(
    (state) => state.assistant?.pendingFocus,
  );

  const onToggleExpanded = () => {
    cookies.set('sidebar_expanded', !expanded, getCookieOptions());
    setExpanded(!expanded);
    resetFullSizeSidebar();
  };

  const resetFullSizeSidebar = useCallback(() => {
    if (!expanded) {
      const currentResizer = document.querySelector('#sidebar');
      const sidebarContainer =
        currentResizer.getElementsByClassName('sidebar-container')[0];
      sidebarContainer.classList.remove('full-size');
      sidebarContainer.classList.remove('no-toolbar');
      setshowFull(true);
    }
  }, [expanded]);

  const onToggleFullSize = useCallback(() => {
    const currentResizer = document.querySelector('#sidebar');
    const sidebarContainer =
      currentResizer.getElementsByClassName('sidebar-container')[0];

    if (showFull) {
      sidebarContainer.classList.add('full-size');
      if (!toolbarExpanded) {
        sidebarContainer.classList.add('no-toolbar');
      } else {
        sidebarContainer.classList.remove('no-toolbar');
      }
    } else {
      sidebarContainer.classList.remove('full-size');
      sidebarContainer.classList.remove('no-toolbar');
    }
    setshowFull(!showFull);
  }, [showFull, toolbarExpanded]);

  const onTabChange = (event, data) => {
    event.nativeEvent.stopImmediatePropagation();
    dispatch(setSidebarTab(data.activeIndex));
  };

  const panes = [
    !!documentTab && {
      menuItem: {
        key: 'documentTab',
        as: 'button',
        className: 'ui button',
        type: 'button',
        content: type || intl.formatMessage(messages.document),
      },
      pane: (
        <Tab.Pane
          key="metadata"
          className="tab-wrapper"
          id="sidebar-metadata"
        />
      ),
    },
    !!blockTab && {
      menuItem: {
        key: 'blockTab',
        as: 'button',
        className: 'ui button',
        type: 'button',
        content: intl.formatMessage(messages.block),
      },
      pane: (
        <Tab.Pane
          key="properties"
          className="tab-wrapper"
          id="sidebar-properties"
        >
          <Icon className="tab-forbidden" name={forbiddenSVG} size="48px" />
        </Tab.Pane>
      ),
    },
    !!orderTab && {
      menuItem: intl.formatMessage(messages.order),
      pane: (
        <Tab.Pane key="order" className="tab-wrapper" id="sidebar-order">
          <Icon className="tab-forbidden" name={forbiddenSVG} size="48px" />
        </Tab.Pane>
      ),
    },
    !!settingsTab && {
      menuItem: intl.formatMessage(messages.settings),
      pane: (
        <Tab.Pane key="settings" className="tab-wrapper" id="sidebar-settings">
          <Icon className="tab-forbidden" name={forbiddenSVG} size="48px" />
        </Tab.Pane>
      ),
    },
    assistantEnabled && {
      menuItem: {
        key: 'assistantTab',
        as: 'button',
        className: 'ui button',
        type: 'button',
        content: intl.formatMessage(messages.assistant),
      },
      pane: (
        <Tab.Pane
          key="assistant"
          className="tab-wrapper"
          id="sidebar-assistant"
        >
          <AssistantPanel host="sidebar" />
        </Tab.Pane>
      ),
    },
  ].filter((pane) => pane);

  const assistantTabIndex = assistantEnabled ? panes.length - 1 : null;

  // The shadowed Block/Edit and BlocksForm exempt this tab index from the
  // automatic switch-to-block-settings on selection (like core exempts
  // the Order tab), so the chat stays open while blocks are selected.
  React.useEffect(() => {
    dispatch(setAssistantTabIndex(assistantTabIndex));
    return () => {
      dispatch(setAssistantTabIndex(null));
    };
  }, [assistantTabIndex, dispatch]);

  React.useEffect(() => {
    if (assistantPendingFocus && assistantTabIndex != null) {
      if (!expanded) {
        cookies.set('sidebar_expanded', true, getCookieOptions());
        setExpanded(true);
      }
      dispatch(setSidebarTab(assistantTabIndex));
      dispatch(setAssistantPendingFocus(false));
    }
  }, [assistantPendingFocus, assistantTabIndex, expanded, cookies, dispatch]);

  return (
    <Fragment>
      <BodyClass
        className={expanded ? 'has-sidebar' : 'has-sidebar-collapsed'}
      />
      <div
        className={cx('sidebar-container', { collapsed: !expanded })}
        style={size > 0 ? { width: size } : null}
      >
        <Button
          type="button"
          aria-label={
            expanded
              ? intl.formatMessage(messages.shrinkSidebar)
              : intl.formatMessage(messages.expandSidebar)
          }
          className={
            content && content.review_state
              ? `${content.review_state} trigger`
              : 'trigger'
          }
          onClick={onToggleExpanded}
        />
        <Button
          type="button"
          className="full-size-sidenav-btn"
          onClick={onToggleFullSize}
          aria-label="full-screen-sidenav"
        >
          <Icon
            className="full-size-icon"
            name={showFull ? expandSVG : collapseSVG}
          />
        </Button>
        <Tab
          menu={{
            secondary: true,
            pointing: true,
            attached: true,
            tabular: true,
            className: 'formtabs',
          }}
          className="tabs-wrapper"
          renderActiveOnly={false}
          activeIndex={tab}
          onTabChange={onTabChange}
          panes={panes}
        />
      </div>
      <div className={expanded ? 'pusher expanded' : 'pusher'} />
    </Fragment>
  );
};

Sidebar.propTypes = {
  documentTab: PropTypes.bool,
  blockTab: PropTypes.bool,
  settingsTab: PropTypes.bool,
};

Sidebar.defaultProps = {
  documentTab: true,
  blockTab: true,
  settingsTab: false,
};

export default compose(withCookies)(Sidebar);
